/**
 * HLOS-138 — useConversationPhase (singleton para estado de fase do conversation).
 *
 * Define a transição `init → chat` (ou `init → error`) durante o boot
 * do ConversationState. Module-level `ref` garante singleton entre
 * ConversationInit.vue (consumidor que seta) e ConversationState.vue
 * (wrapper que lê), mesmo pattern de `useBootOrchestrator`.
 *
 * **Constitucional:**
 *   - **CL-09 (Explicabilidade):** `phase` ref rastreável
 *   - **CL-14 (Code quality):** separation of concerns (boot phase
 *     isolado do state machine principal em `types.ts`)
 *   - **Princípio V (Aprendiz Decide):** phase `'error'` permite saída
 *     (CC-05: zero penalidade)
 *
 * **Ref:**
 *   - HLOS-138 (este arquivo)
 *   - HLOS-15 (Pyodide lazy init + indicators)
 *   - HLOS-137 (próxima: implementação baseada neste design)
 */

import { ref } from "vue";

/**
 * Fase do ciclo de vida do conversation:
 *   - `'init'`  → boot em progresso (Pyodide + master key + webllm)
 *   - `'chat'`  → pronto, usuário pode interagir
 *   - `'error'` → boot falhou, wrapper deve mostrar fallback
 */
export type ConversationPhase = "init" | "chat" | "error";

/** Phase atual — singleton module-level (mesmo pattern de `useBootOrchestrator`). */
const phase = ref<ConversationPhase>("init");

/** Mensagem de erro (quando `phase === 'error'`). `null` caso contrário. */
const errorMessage = ref<string | null>(null);

/** Reseta para o estado inicial (útil em tests). */
function reset(): void {
  phase.value = "init";
  errorMessage.value = null;
}

/** Marca boot como completo. Idempotente. */
function markReady(): void {
  phase.value = "chat";
  errorMessage.value = null;
}

/** Marca boot como falho. Define mensagem de erro. */
function markError(message: string): void {
  phase.value = "error";
  errorMessage.value = message;
}

/**
 * Composable Vue 3 que retorna refs reativas para o estado de phase.
 * Consumido por:
 *   - ConversationInit.vue (chama `markReady()` / `markError()`)
 *   - ConversationState.vue (lê `phase` para decidir qual filho renderizar)
 */
export function useConversationPhase(): {
  phase: typeof phase;
  errorMessage: typeof errorMessage;
  markReady: () => void;
  markError: (message: string) => void;
  reset: () => void;
} {
  return {
    phase,
    errorMessage,
    markReady,
    markError,
    reset,
  };
}

/** Exports auxiliares (úteis para tests). */
export { phase, errorMessage, markReady, markError, reset };
